import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { track } from '../lib/amplitude';
import { errorMessage } from '../lib/api';
import type { Tag } from '../lib/types';
import { qk } from '../queries/keys';
import { addVideoTag, fetchVideoTags, removeVideoTag, type TagChoice } from '../queries/tags';
import { TagPicker } from './TagPicker';
import { XIcon } from './icons';
import { SCOPE_TITLE, TagScopeIcon } from './TagScope';
import { ErrorText, cx } from './ui';

/**
 * The tags on one recording, for somebody who has it in their library: the
 * ones there, each with a way off, and the picker for the next one. Team
 * tags carry the people icon; a personal one the lock, and is drawn
 * dashed, because it is the one thing on the page nobody else can see.
 */
export function TagEditor({
  videoId,
  shareId,
  inTeamLibrary,
  initial,
}: {
  videoId: string;
  /** For the event, never the id. */
  shareId: string;
  /** Whether a team tag may be made for it. */
  inTeamLibrary: boolean;
  initial?: Tag[];
}) {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const query = useQuery({
    queryKey: qk.videoTags(videoId),
    queryFn: () => fetchVideoTags(videoId),
    initialData: initial ? { tags: initial.map((tag) => ({ ...tag, videoCount: 0 })) } : undefined,
  });
  const tags = query.data?.tags ?? [];

  const settle = (next: { tags: Tag[] }) => {
    setError(null);
    client.setQueryData(qk.videoTags(videoId), next);
    void client.invalidateQueries({ queryKey: ['tags'] });
    void client.invalidateQueries({ queryKey: ['videos'] });
    void client.invalidateQueries({ queryKey: qk.video(videoId) });
  };

  const add = useMutation({
    mutationFn: (choice: TagChoice) => addVideoTag(videoId, choice),
    onSuccess: settle,
    onError: (cause) => setError(errorMessage(cause)),
  });
  const remove = useMutation({
    mutationFn: (tagId: string) => removeVideoTag(videoId, tagId),
    onSuccess: settle,
    onError: (cause) => setError(errorMessage(cause)),
  });

  return (
    <div>
      {tags.length > 0 ? (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <li
              key={tag.id}
              title={SCOPE_TITLE[tag.scope]}
              className={cx(
                'flex items-center gap-1 rounded-full border bg-slate-900 py-0.5 pl-2.5 pr-1 text-caption text-chalk',
                tag.scope === 'personal' ? 'border-dashed border-chalk/30' : 'border-hairline',
              )}
            >
              <TagScopeIcon scope={tag.scope} size={10} className="text-chalk-faint" />
              <span className="max-w-[12rem] truncate">{tag.name}</span>
              <button
                type="button"
                aria-label={`Remove ${tag.name}`}
                disabled={remove.isPending}
                onClick={() => {
                  track('Tag Removed', { video_id: shareId, scope: tag.scope });
                  remove.mutate(tag.id);
                }}
                className="flex h-4 w-4 items-center justify-center rounded-full text-chalk-faint hover:bg-slate-700 hover:text-chalk disabled:pointer-events-none disabled:opacity-40"
              >
                <XIcon size={10} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <TagPicker
        exclude={tags.map((tag) => tag.id)}
        canCreateTeam={inTeamLibrary}
        disabled={add.isPending}
        onPick={(choice, row) => {
          track('Tag Added', { video_id: shareId, scope: row.scope, created: row.created });
          add.mutate(choice);
        }}
      />
      {error ? <ErrorText>{error}</ErrorText> : null}
      {tags.length === 0 && !query.isLoading ? (
        <div className="py-10 text-center">
          <p className="text-h4 text-chalk">No tags yet</p>
          <p className="mx-auto mt-1.5 max-w-xs text-caption text-chalk-dim">
            Tag this recording to find it again in your library.{' '}
            {inTeamLibrary
              ? 'Only you see a personal tag; a team tag is shared with everyone on your team.'
              : 'Only you see the tags you add here.'}
          </p>
        </div>
      ) : null}
    </div>
  );
}
